/**
 * Un tab ramas deschis peste un deploy ruleaza mai departe codul vechi, oricat — are deja tot
 * bundle-ul in memorie, deci nu pica nimic si `vite:preloadError` nu se declanseaza. Pe 09.10 un
 * admin a lucrat o dimineata intreaga pe versiunea de dinaintea reparatiilor din seara precedenta.
 *
 * Build-ul scrie `/version.json` cu identificatorul lui (plugin-ul `versiuneBuild` din
 * `vite.config.js`), iar acelasi identificator e inclus in bundle. Cand cele doua difera, pagina
 * se reincarca la URMATOAREA navigare.
 *
 * 🔴 Niciodata pe loc: revenirea in tab e exact momentul in care un profesor se intoarce din
 * galeria telefonului cu pozele activitatii. La navigare, ecranul curent se paraseste oricum.
 */

import { reincarcaOData } from './reincarcare';

/** Cel mult o cerere la atat; `version.json` are cativa octeti, dar revenirile in tab sunt dese. */
export const INTERVAL_VERIFICARE_MS = 5 * 60_000;

/**
 * Daca reincarcarea n-a adus build-ul anuntat (HTML vechi servit inca o data), nu mai incercam
 * pentru acelasi build decat dupa atat — altfel fiecare click din meniu ar reincarca pagina.
 */
export const FEREASTRA_ACELASI_BUILD_MS = 10 * 60_000;

export const BUILD_LOCAL: string | undefined = import.meta.env.VITE_BUILD_ID || undefined;

export function trebuieVerificat(
    acum: number,
    ultimaVerificare: number | null,
    intervalMs: number = INTERVAL_VERIFICARE_MS
): boolean {
    return ultimaVerificare === null || acum - ultimaVerificare >= intervalMs;
}

/**
 * Identificatorul build-ului publicat, daca e ALTUL decat cel incarcat; altfel `null`.
 * Orice raspuns care nu e `{ "build": "<sir nevid>" }` inseamna „nu stiu", nu „versiune noua":
 * fara fisier, SWA intoarce `index.html` (navigationFallback), iar un proxy poate intoarce orice.
 */
export function buildNouDin(raspuns: unknown, buildLocal: string | undefined): string | null {
    if (!buildLocal) return null;
    if (typeof raspuns !== 'object' || raspuns === null) return null;
    const build = (raspuns as { build?: unknown }).build;
    if (typeof build !== 'string' || build.trim() === '') return null;
    return build === buildLocal ? null : build;
}

/**
 * Se reincarca la navigarea asta? Doar daca navigarea nu cara stare: pasii fluxurilor cu cod
 * (cerere → verificare) si reinscrierea isi trec datele prin `location.state` si prin memorie,
 * iar o reincarcare intre ei ar rupe fluxul. Navigarile din meniu nu au stare.
 */
export function seReincarcaLaNavigare(buildNou: string | null, stareNavigare: unknown): boolean {
    return buildNou !== null && (stareNavigare === null || stareNavigare === undefined);
}

let ultimaVerificare: number | null = null;
let buildNou: string | null = null;

async function verifica(): Promise<void> {
    if (!BUILD_LOCAL || buildNou) return;
    const acum = Date.now();
    if (!trebuieVerificat(acum, ultimaVerificare)) return;
    ultimaVerificare = acum;
    try {
        const raspuns = await fetch(`/version.json?t=${acum}`, { cache: 'no-store' });
        if (!raspuns.ok) return;
        buildNou = buildNouDin(await raspuns.json(), BUILD_LOCAL);
    } catch {
        // Fara retea sau raspuns care nu e JSON: ramanem pe ce avem, incercam data viitoare.
    }
}

/** De chemat la fiecare schimbare de ruta, cu `location.state` al rutei noi. */
export function laNavigare(stareNavigare: unknown): void {
    if (seReincarcaLaNavigare(buildNou, stareNavigare) && reincarcaOData(`versiune:${buildNou}`, FEREASTRA_ACELASI_BUILD_MS)) return;
    void verifica();
}

/** `visibilitychange`, nu `focus`: pe mobil e singurul semnal de incredere la revenirea in tab. */
export function pornesteVerificareaVersiunii(): void {
    if (!BUILD_LOCAL) return;
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void verifica();
    });
}
